import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useUserRoles } from 'hooks/useUserRoles'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import validator from 'validator'
import userService from 'services/user'
import ActionsModal from 'components/ActionsModal'
import { selectDisplayedUser, updateDisplayedUser, closeDisplayedUser, updateUser } from 'store/organizations'
import { selectCurrentUser, selectOrganizations, selectUsers, selectAllGroups, fetchGroups } from 'store/global'
import apiService from 'services/api'
import messages from './messages'
import DropdownInput from 'components/DropdownInput'
import styles from './UserModal.scss'

const allowedModes = ['create', 'edit', 'view']
const [MODE_CREATE, MODE_EDIT, MODE_VIEW] = allowedModes

function UserModal({ intl, mode = MODE_CREATE, onSubmit }) {
  const dispatch = useDispatch()
  const userRoles = useUserRoles()

  const allUsers = useSelector(selectUsers)
  const groupsList = useSelector(selectAllGroups)
  const currentUser = useSelector(selectCurrentUser)
  const displayedUser = useSelector(selectDisplayedUser)
  const organizations = useSelector(selectOrganizations)

  const isSuperuser = userService.isSuperuser(currentUser)
  const isAdmin = userService.isAdmin(currentUser)

  const [userGroups, setUserGroups] = useState([])
  const [isLoadingUserGroups, setIsLoadingUserGroups] = useState(false)
  const flattenGroupsForOptions = (groups, level = 0) => {
    let allGroups = []
    groups.forEach(group => {
      allGroups.push({
        value: group.id,
        label: '  '.repeat(level) + group.path,
        path: group.path,
      })
      if (group.subGroups && group.subGroups.length > 0) {
        allGroups = allGroups.concat(flattenGroupsForOptions(group.subGroups, level + 1))
      }
    })
    return allGroups
  }

  const groupOptions = flattenGroupsForOptions(groupsList || [])

  const groupSelectedOptions = useMemo(() => {
    if (!displayedUser || !groupsList) {
      return []
    }
    let userGroupIds = []
    if (mode === MODE_EDIT) {
      userGroupIds = displayedUser.groupIds || []
    } else if (mode === MODE_VIEW) {
      userGroupIds = userGroups.length > 0 ? userGroups.map(group => group.id) : []
    } else {
      userGroupIds = displayedUser.groupIds || []
    }

    const flattenGroupsForLookup = groups => {
      let allGroups = []
      groups.forEach(group => {
        allGroups.push(group)
        if (group.subGroups && group.subGroups.length > 0) {
          allGroups = allGroups.concat(flattenGroupsForLookup(group.subGroups))
        }
      })
      return allGroups
    }

    const allGroupsFlat = flattenGroupsForLookup(groupsList || [])

    return userGroupIds
      .map(groupId => {
        const group = allGroupsFlat.find(group => group.id === groupId)
        return group ? { value: groupId, label: group.path } : null
      })
      .filter(option => option !== null)
  }, [displayedUser, groupsList, userGroups, mode])

  useEffect(() => {
    if (!groupsList || groupsList.length === 0) {
      dispatch(fetchGroups())
    }
  }, [dispatch, groupsList])

  useEffect(() => {
    const fetchUserGroups = async () => {
      if ((mode === MODE_EDIT || mode === MODE_VIEW) && displayedUser?.id) {
        setIsLoadingUserGroups(true)
        try {
          const response = await apiService.getUserGroups(displayedUser.id)
          const groups = response.groups || []
          setUserGroups(groups)

          if (mode === MODE_EDIT && groups.length > 0) {
            const groupIds = groups.map(group => group.id)
            dispatch(updateDisplayedUser({ key: 'groupIds', value: groupIds }))
          }
        } catch (error) {
          setUserGroups([])
        } finally {
          setIsLoadingUserGroups(false)
        }
      }
    }

    fetchUserGroups()
  }, [mode, displayedUser?.id, dispatch])

  useEffect(() => {
    if (!organizations || organizations.length === 0 || !userRoles || userRoles.length === 0) {
      return
    }

    const [firstOrganization] = organizations
    const basicUser = userRoles.find(({ name }) => name === 'user')

    if (!firstOrganization || !basicUser) {
      return
    }

    if (isAdmin && mode === MODE_CREATE) {
      dispatch(
        updateDisplayedUser({
          key: 'organization',
          value: firstOrganization.id,
        }),
      )
      return
    }

    if ([MODE_VIEW, MODE_EDIT].includes(mode)) {
      if (isSuperuser) {
        const { organizationId = firstOrganization.id } = displayedUser
        dispatch(updateDisplayedUser({ key: 'organization', value: organizationId }))
      }

      const { roleId = basicUser.id } = displayedUser
      dispatch(updateDisplayedUser({ key: 'role', value: roleId }))

      if (userGroups.length > 0 && mode === MODE_VIEW) {
        const groupIds = userGroups.map(group => group.id)
        dispatch(updateDisplayedUser({ key: 'groupIds', value: groupIds }))
      }
    }
  }, [organizations, userRoles, isSuperuser, mode, displayedUser, userGroups, dispatch])

  const onCloseModal = () => dispatch(closeDisplayedUser())

  const onFieldChange = (key, value) => dispatch(updateDisplayedUser({ key, value }))

  const onFormSubmit = displayedUser => {
    const userData = {
      ...displayedUser,
      groupIds: groupSelectedOptions.map(option => option.value),
    }

    if (mode === MODE_CREATE) {
      onSubmit(userData)
    } else if (mode === MODE_EDIT) {
      dispatch(updateUser({ user: userData }))
    }
  }

  const onGroupsChange = (key, nextGroupsSelections) => {
    const nextGroupIds = nextGroupsSelections.map(({ value }) => value)
    onFieldChange(key, nextGroupIds)

    dispatch(updateDisplayedUser({ key: 'groupIds', value: nextGroupIds }))
  }

  const isEmailValidAndAvailable = email => validator.isEmail(email) && allUsers.every(user => user.email !== email)

  const fields = {
    organization: {
      key: 'organization',
      type: 'select',
      label: <FormattedMessage {...messages.labels.organization} />,
      options: organizations || [],
      isRequired: true,
      disabled: mode === MODE_VIEW || mode === MODE_EDIT,
      renderInput: () => {
        if (mode === MODE_VIEW || mode === MODE_EDIT) {
          const userOrganization = organizations?.find(org => org.id === displayedUser.organization)?.name || displayedUser.organization || 'No organization assigned'
          return (
            <div className={styles.readOnlyField}>
              {userOrganization}
            </div>
          )
        }
        return null
      },
    },
    email: {
      key: 'email',
      label: <FormattedMessage {...messages.labels.email} />,
      isRequired: true,
      disabled: mode !== MODE_CREATE,
      validator: isEmailValidAndAvailable,
      formFeedback: (
        <FormattedMessage
          {...messages.formFeedback[validator.isEmail(displayedUser.email || '') ? 'emailUnavailable' : 'invalidEmail']}
        />
      ),
    },
    firstName: {
      key: 'firstName',
      label: <FormattedMessage {...messages.labels.firstName} />,
      isRequired: true,
      validator: value => value.length > 0,
    },
    lastName: {
      key: 'lastName',
      label: <FormattedMessage {...messages.labels.lastName} />,
      isRequired: true,
      validator: value => value.length > 0,
    },
    groups: {
      key: 'groupIds',
      isRequired: false,
      label: <FormattedMessage {...messages.labels.groups} />,
      disabled: mode === MODE_VIEW,
      renderInput: () => {
        if (isLoadingUserGroups) {
          return <div>Loading groups...</div>
        }

        if (mode === MODE_VIEW) {
          const groupPaths = userGroups.map(group => group.path)
          return (
            <div className={styles.readOnlyField}>
              {groupPaths.length > 0 ? groupPaths.join(', ') : 'No groups assigned'}
            </div>
          )
        }

        return (
          <div>
            <DropdownInput
              options={groupOptions}
              selections={groupSelectedOptions}
              onChange={nextGroupsSelections => {
                onGroupsChange('groupIds', nextGroupsSelections)
              }}
              hasSelectAll={false}
              disableSearch={false}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Selected: {groupSelectedOptions.length} groups
            </div>
          </div>
        )
      },
    },
  }

  return (
    <ActionsModal
      type='user'
      mode={mode}
      item={displayedUser}
      primaryKey='email'
      fields={fields}
      onFieldChange={onFieldChange}
      onClose={onCloseModal}
      onSubmit={() => onFormSubmit(displayedUser)}
    ></ActionsModal>
  )
}

UserModal.propTypes = {
  mode: PropTypes.oneOf(allowedModes),
  onSubmit: PropTypes.func.isRequired,
}

export default injectIntl(UserModal)
